import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { QrCode, X } from "lucide-react";

const READER_ID = "assetiq-qr-reader";

/**
 * Camera-based QR scanner. Calls `onDetected(text)` with the raw decoded string.
 * Auto-parses if the payload is a JSON like {"asset_id":"..."} — passes just the asset_id.
 */
export default function QRScannerButton({ onDetected, label = "Scan QR", size = "default" }) {
  const [open, setOpen] = useState(false);
  const scannerRef = useRef(null);
  const containerRef = useRef(null);
  const [cameraError, setCameraError] = useState("");

  useEffect(() => {
    if (!open) return;
    let active = true;

    (async () => {
      try {
        // Small delay so the dialog & div are mounted
        await new Promise((r) => setTimeout(r, 100));
        if (!active) return;

        const el = document.getElementById(READER_ID);
        if (!el) return;

        const html5Qr = new Html5Qrcode(READER_ID, { verbose: false });
        scannerRef.current = html5Qr;

        const cams = await Html5Qrcode.getCameras();
        if (!cams || cams.length === 0) {
          setCameraError("No camera detected on this device.");
          return;
        }
        // Prefer back camera on mobile
        const back = cams.find((c) => /back|rear|environment/i.test(c.label));
        const camId = (back || cams[0]).id;

        await html5Qr.start(
          camId,
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decoded) => {
            let value = decoded;
            try {
              const parsed = JSON.parse(decoded);
              if (parsed && parsed.asset_id) value = parsed.asset_id;
            } catch { /* not JSON – use raw */ }
            html5Qr.stop().catch(() => {});
            setOpen(false);
            onDetected(value);
          },
          () => { /* scan errors are noisy - ignore */ }
        );
      } catch (e) {
        setCameraError(e?.message || "Failed to start camera. Please allow camera permission.");
      }
    })();

    return () => {
      active = false;
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
      }
    };
  }, [open, onDetected]);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size={size}
        onClick={() => { setCameraError(""); setOpen(true); }}
        data-testid="open-qr-scanner"
      >
        <QrCode className="w-4 h-4 mr-2" /> {label}
      </Button>
      <Dialog open={open} onOpenChange={(v) => { if (!v && scannerRef.current) scannerRef.current.stop().catch(() => {}); setOpen(v); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><QrCode className="w-4 h-4 text-[#7C1D3A]" /> Scan Asset QR</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {cameraError ? (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
                {cameraError}
                <div className="text-xs text-slate-600 mt-2">On desktop browsers, you'll need to allow camera access when prompted. On iOS/Android, ensure camera permission is granted for this site.</div>
              </div>
            ) : (
              <div className="text-xs text-slate-500 text-center">Point the camera at the asset's QR label — it will auto-detect.</div>
            )}
            <div id={READER_ID} ref={containerRef} className="rounded-lg overflow-hidden bg-slate-900" style={{ minHeight: cameraError ? 0 : 240 }} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
